import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, Sparkles, Trash2, Plus, ArrowLeft, FileCheck2, Image as ImageIcon, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import AppLayout from "@/components/AppLayout";

const TYPES = [
  { v: "arithmetic", label: "Arithmetic (a + b − c = ?)" },
  { v: "read", label: "Read the beads (type the number)" },
  { v: "represent", label: "Show the number on abacus" },
];

function OperationChips({ operations, onChange }) {
  const update = (i, field, val) => {
    const next = operations.map((o, idx) => (idx === i ? { ...o, [field]: val } : o));
    onChange(next);
  };
  const remove = (i) => onChange(operations.filter((_, idx) => idx !== i));
  const add = () => onChange([...(operations || []), { op: "+", value: 1 }]);
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {(operations || []).map((o, i) => (
        <div key={i} className="inline-flex items-center gap-1 bg-canvas rounded-xl px-1.5 py-1">
          <select value={o.op} onChange={(e) => update(i, "op", e.target.value)} className="w-11 h-8 text-sm font-bold bg-white rounded-lg border border-cloud focus:border-sky focus:outline-none text-center">
            <option value="+">+</option>
            <option value="-">−</option>
            <option value="×">×</option>
            <option value="÷">÷</option>
          </select>
          <input type="number" min="1" value={o.value} onChange={(e) => update(i, "value", parseInt(e.target.value, 10) || 0)} className="w-14 h-8 text-sm font-semibold bg-white rounded-lg border border-cloud focus:border-sky focus:outline-none text-center" />
          <button type="button" onClick={() => remove(i)} className="text-stone hover:text-watermelon p-0.5"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      <button type="button" onClick={add} className="inline-flex items-center gap-1 text-xs font-bold text-sky hover:text-sky-hover px-2 h-8 rounded-xl border-2 border-dashed border-cloud hover:border-sky">
        <Plus className="w-3 h-3" /> op
      </button>
    </div>
  );
}

export default function AdminExerciseFromPhoto() {
  const navigate = useNavigate();
  const fileInput = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [hint, setHint] = useState("");
  const [busy, setBusy] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [meta, setMeta] = useState({ title: "", level: 1, week: 1, topic: "", description: "", presentation_mode: "sequential" });

  const pickFile = (f) => {
    if (!f) return;
    if (!/^image\/(jpeg|jpg|png|webp)$/i.test(f.type)) {
      toast.error("Please pick a JPEG, PNG, or WEBP photo.");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Image is larger than 10 MB. Please compress.");
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setQuestions([]);
  };

  const extract = async () => {
    if (!file) { toast.error("Please choose a worksheet photo first."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      if (hint.trim()) fd.append("hint", hint.trim());
      const { data } = await api.post("/admin/exercises/extract-from-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setQuestions((data.questions || []).map((q, idx) => ({ ...q, _localId: `q-${idx}-${Date.now()}` })));
      toast.success(`AI read ${data.count} question${data.count === 1 ? "" : "s"}. Review and edit before publishing.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const updateQ = (idx, patch) => setQuestions((prev) => prev.map((q, i) => (i === idx ? { ...q, ...patch } : q)));
  const removeQ = (idx) => setQuestions((prev) => prev.filter((_, i) => i !== idx));
  const addQ = () => setQuestions((prev) => [...prev, { _localId: `q-${Date.now()}`, type: "arithmetic", starting_number: 0, operations: [], correct_answer: 0 }]);

  const save = async (publish) => {
    if (!meta.title.trim()) { toast.error("Please give the exercise a title."); return; }
    if (!meta.topic.trim()) { toast.error("Please add a topic."); return; }
    if (questions.length === 0) { toast.error("Add at least one question."); return; }
    // Infer exercise type: mixed → "arithmetic"; all same → that type
    const types = Array.from(new Set(questions.map((q) => q.type)));
    const exerciseType = types.length === 1 ? types[0] : "arithmetic";
    const payload = {
      title: meta.title.trim(),
      level: Number(meta.level) || 1,
      week: Number(meta.week) || 1,
      topic: meta.topic.trim(),
      description: meta.description.trim(),
      type: exerciseType,
      is_published: !!publish,
      presentation_mode: meta.presentation_mode,
      questions: questions.map((q, idx) => ({
        starting_number: Number(q.starting_number) || 0,
        operations: q.type === "arithmetic" ? (q.operations || []).map((o) => ({ op: o.op, value: Number(o.value) || 0 })).filter((o) => o.value > 0) : [],
        order: idx + 1,
      })),
    };
    setBusy(true);
    try {
      const { data } = await api.post("/admin/exercises", payload);
      toast.success(publish ? "Exercise published!" : "Saved as draft.");
      navigate(`/admin/exercises/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <AppLayout variant="admin">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <button data-testid="back-to-exercises" onClick={() => navigate("/admin/exercises")} className="inline-flex items-center gap-1.5 text-sm font-bold text-stone hover:text-ink mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to exercises
        </button>
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-lemon/40 text-ink font-bold text-xs mb-3">
            <Sparkles className="w-3.5 h-3.5" /> AI-powered
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-ink">Create exercise from photo</h1>
          <p className="text-stone mt-1">Snap a page from your workbook — AI reads every question. You review, edit, publish.</p>
        </div>

        {/* Step 1 — upload + extract */}
        <div className="p-6 bg-white rounded-3xl card-tactile mb-6">
          <h2 className="font-heading text-xl font-bold text-ink mb-4">1. Upload the worksheet page</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div
              data-testid="dropzone"
              onClick={() => fileInput.current?.click()}
              onDragOver={(e) => { e.preventDefault(); }}
              onDrop={(e) => { e.preventDefault(); pickFile(e.dataTransfer.files?.[0]); }}
              className="cursor-pointer border-2 border-dashed border-cloud rounded-2xl p-8 text-center hover:border-sky hover:bg-sky-light/30 transition"
            >
              <input ref={fileInput} data-testid="photo-input" type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => pickFile(e.target.files?.[0])} />
              {preview ? (
                <img src={preview} alt="Worksheet preview" className="max-h-64 mx-auto rounded-xl mb-3" />
              ) : (
                <ImageIcon className="w-12 h-12 mx-auto text-stone mb-3" />
              )}
              <div className="text-sm text-stone font-semibold">
                {file ? file.name : "Click or drop a JPEG / PNG / WEBP"}
              </div>
              <div className="text-xs text-stone mt-1">Max 10 MB. One worksheet page per exercise.</div>
            </div>
            <div className="flex flex-col justify-between">
              <div className="text-sm text-stone space-y-2">
                <p><strong className="text-ink">Tip:</strong> good lighting + a flat page + clear handwriting = fewer OCR mistakes.</p>
                <p>Supported question types: arithmetic (<code>4 + 3 + 2 = ?</code>), "read the beads", "show the number".</p>
                <p>You'll review and fix any mistakes before publishing to students.</p>
              </div>
              <button
                data-testid="extract-button"
                onClick={extract}
                disabled={!file || busy}
                className="mt-4 inline-flex items-center justify-center gap-2 h-14 rounded-full bg-watermelon text-white font-heading font-bold text-lg btn-tactile hover:bg-watermelon-hover disabled:opacity-60"
              >
                {busy ? <><Loader2 className="w-5 h-5 animate-spin" /> Reading…</> : <><Sparkles className="w-5 h-5" /> Extract questions with AI</>}
              </button>
            </div>
          </div>
          <div className="mt-4">
            <label htmlFor="hint-input" className="block text-sm font-bold text-ink mb-1">Hint for AI (optional)</label>
            <input
              id="hint-input"
              data-testid="hint-input"
              value={hint}
              onChange={(e) => setHint(e.target.value)}
              placeholder='e.g. "only extract the 30 multiplications on the right side" or "treat each column A–E as one question"'
              className="w-full h-11 px-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none text-sm"
            />
            <p className="text-xs text-stone mt-1">Tell the AI exactly which part of the page to read — super helpful for busy pages or mixed layouts.</p>
          </div>
        </div>

        {/* Step 2 — review */}
        {questions.length > 0 && (
          <div className="p-6 bg-white rounded-3xl card-tactile mb-6" data-testid="review-section">
            <h2 className="font-heading text-xl font-bold text-ink mb-4 flex items-center gap-2">
              <FileCheck2 className="w-5 h-5 text-leaf" /> 2. Review the {questions.length} extracted questions
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
              {questions.map((q, idx) => (
                <div key={q._localId} data-testid={`extracted-q-${idx}`} className="bg-canvas rounded-2xl p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-ink text-white font-bold text-xs">{idx + 1}</span>
                    <button data-testid={`remove-q-${idx}`} onClick={() => removeQ(idx)} className="text-stone hover:text-watermelon p-1"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <select data-testid={`q-type-${idx}`} value={q.type} onChange={(e) => updateQ(idx, { type: e.target.value, operations: e.target.value === "arithmetic" ? (q.operations || []) : [] })} className="w-full h-9 text-xs font-semibold bg-white rounded-xl border-2 border-cloud focus:border-sky focus:outline-none px-2 mb-2">
                    {TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                  </select>
                  <div className="flex items-center gap-2 mb-2">
                    <label className="text-xs font-bold text-stone w-12">{q.type === "arithmetic" ? "Start:" : "Number:"}</label>
                    <input data-testid={`q-start-${idx}`} type="number" value={q.starting_number} onChange={(e) => updateQ(idx, { starting_number: parseInt(e.target.value, 10) || 0 })} className="flex-1 h-9 text-sm font-bold bg-white rounded-xl border-2 border-cloud focus:border-sky focus:outline-none px-2 text-center" />
                  </div>
                  {q.type === "arithmetic" && (
                    <OperationChips operations={q.operations} onChange={(ops) => updateQ(idx, { operations: ops })} />
                  )}
                </div>
              ))}
            </div>
            <button data-testid="add-question" onClick={addQ} className="inline-flex items-center gap-1.5 text-sm font-bold text-sky hover:text-sky-hover">
              <Plus className="w-4 h-4" /> Add question manually
            </button>
          </div>
        )}

        {/* Step 3 — exercise details + publish */}
        {questions.length > 0 && (
          <div className="p-6 bg-white rounded-3xl card-tactile" data-testid="publish-section">
            <h2 className="font-heading text-xl font-bold text-ink mb-4">3. Exercise details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-bold text-ink mb-1">Title</label>
                <input data-testid="meta-title" value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} className="w-full h-11 px-3 rounded-xl border-2 border-cloud focus:border-sky focus:outline-none" placeholder="e.g., Workbook Page 7 – Addition" />
              </div>
              <div>
                <label className="block text-sm font-bold text-ink mb-1">Topic</label>
                <input data-testid="meta-topic" value={meta.topic} onChange={(e) => setMeta({ ...meta, topic: e.target.value })} className="w-full h-11 px-3 rounded-xl border-2 border-cloud focus:border-sky focus:outline-none" placeholder="e.g., Two-digit addition" />
              </div>
              <div>
                <label className="block text-sm font-bold text-ink mb-1">Level</label>
                <input data-testid="meta-level" type="number" min="1" max="10" value={meta.level} onChange={(e) => setMeta({ ...meta, level: e.target.value })} className="w-full h-11 px-3 rounded-xl border-2 border-cloud focus:border-sky focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm font-bold text-ink mb-1">Week</label>
                <input data-testid="meta-week" type="number" min="1" max="52" value={meta.week} onChange={(e) => setMeta({ ...meta, week: e.target.value })} className="w-full h-11 px-3 rounded-xl border-2 border-cloud focus:border-sky focus:outline-none" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-ink mb-1">How should students solve it?</label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <label className={`flex-1 flex items-start gap-3 p-3 rounded-2xl cursor-pointer border-2 ${meta.presentation_mode === "sequential" ? "border-sky bg-sky-light/30" : "border-cloud bg-canvas"}`}>
                    <input data-testid="mode-sequential" type="radio" name="mode" value="sequential" checked={meta.presentation_mode === "sequential"} onChange={() => setMeta({ ...meta, presentation_mode: "sequential" })} className="mt-1 w-4 h-4 accent-sky" />
                    <div>
                      <div className="font-bold text-ink text-sm">One at a time</div>
                      <div className="text-xs text-stone">Big question + abacus, Next button (mobile-friendly)</div>
                    </div>
                  </label>
                  <label className={`flex-1 flex items-start gap-3 p-3 rounded-2xl cursor-pointer border-2 ${meta.presentation_mode === "grid" ? "border-sky bg-sky-light/30" : "border-cloud bg-canvas"}`}>
                    <input data-testid="mode-grid" type="radio" name="mode" value="grid" checked={meta.presentation_mode === "grid"} onChange={() => setMeta({ ...meta, presentation_mode: "grid" })} className="mt-1 w-4 h-4 accent-sky" />
                    <div>
                      <div className="font-bold text-ink text-sm">Worksheet grid</div>
                      <div className="text-xs text-stone">All questions on one page, type each answer, submit (closest to the book)</div>
                    </div>
                  </label>
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-ink mb-1">Description (optional)</label>
                <textarea data-testid="meta-description" value={meta.description} onChange={(e) => setMeta({ ...meta, description: e.target.value })} rows={2} className="w-full px-3 py-2 rounded-xl border-2 border-cloud focus:border-sky focus:outline-none" placeholder="Short note shown to students" />
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button data-testid="save-draft" onClick={() => save(false)} disabled={busy} className="h-12 px-5 rounded-full bg-white border-2 border-cloud text-ink font-bold btn-tactile disabled:opacity-60">Save as draft</button>
              <button data-testid="publish-now" onClick={() => save(true)} disabled={busy} className="h-12 px-6 rounded-full bg-watermelon text-white font-bold btn-tactile hover:bg-watermelon-hover disabled:opacity-60">Publish exercise</button>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
